import { useTranslation } from "react-i18next";
import moment from "moment";
import { Link } from "react-router-dom";
import { ArrowRightIcon, HistoryIcon } from "../../../../../components/icons";

const GeneralInformation = ({
    authDetailBooking,
    typo12,
    formatDate,
}) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const isJapanese = ["jp", "ja"].includes((currentLanguage || "").toLowerCase());

    const getTransferTypeLabel = (bookingType, isAirportTransfer) => {
        if (bookingType === "byTheHour") {
            return t("bookingDetail.generalInformation.bookingType.byTheHour");
        } else {
            if (isAirportTransfer) {
                return t("bookingDetail.generalInformation.bookingType.airportTransfer");
            } else {
                return t("bookingDetail.generalInformation.bookingType.specifiedDestination");
            }
        }
    }

    const getLocationDetail = (loc) => {
        const formattedAddressArr = loc?.formatted_address?.split(",");
        const name = formattedAddressArr?.[0];
        const address = formattedAddressArr?.slice(1).join(",");

        return {
            name,
            address
        }
    }

    function minutesToHoursCeil(minutes) {
        return Math.ceil(minutes / 30) * 0.5;
    }

    return (
        <div className="tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-white tw:lg:px-7 tw:py-[30px]">
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:pb-6 tw:px-5 tw:lg:px-0">
                <div className="tw:font-hanken-grotesk tw:text-[16px] tw:font-bold tw:leading-normal tw:text-[#282828]">
                    {t("bookingDetail.generalInformation.title")}
                </div>
                {authDetailBooking?.requestChangeBooking?.filter(item => item.requestType === "reschedule").length > 0 && (
                    <Link
                        to={`/booking-detail/${authDetailBooking?._id}/modification-history`}
                        className="tw:inline-flex tw:items-center tw:gap-[6px] tw:text-[#8d8d8d] tw:!no-underline"
                    >
                        <HistoryIcon size={14} color="#8d8d8d" />
                        <span className={`tw:font-hanken-grotesk tw:underline ${typo12} tw:text-[#8D8D8D]`}>
                            {`${isJapanese ? "変更履歴" : "Modification History"} (${authDetailBooking?.requestChangeBooking?.filter(item => (item.requestType === "reschedule" && item.status !== "DRAFT")).length || 0})`}
                        </span>
                        <ArrowRightIcon size={14} color="#8d8d8d" />
                    </Link>
                )}
            </div>
            <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
            <div className="tw:flex tw:flex-col tw:gap-y-4 tw:lg:gap-[14px] tw:pt-6 tw:px-5 tw:lg:px-0">
                <div className="tw:grid tw:grid-cols-4 tw:gap-x-4 tw:lg:gap-x-[54px] tw:gap-y-4 tw:lg:gap-y-0">
                    <div className="tw:col-span-4 tw:lg:col-span-1 tw:flex tw:w-[200px] tw:flex-col tw:gap-[3px]">
                        <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.bookingId")}</span>
                        <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                            {`#${authDetailBooking?.key}`}
                        </p>
                    </div>
                    <div className="tw:col-span-4 tw:h-px tw:w-full tw:bg-[#e8e8e8] tw:block tw:lg:hidden" />
                    <div className="tw:col-span-2 tw:lg:col-span-1 tw:flex tw:w-full tw:lg:w-[200px] tw:flex-col tw:gap-[3px]">
                        <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.passengerName")}</span>
                        <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                            {`${authDetailBooking?.passengerPronounced} ${authDetailBooking?.passengerName}`}
                        </p>
                    </div>
                    <div className="tw:col-span-2 tw:lg:col-span-2 tw:flex tw:flex-col tw:gap-[3px]">
                        <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.transferType")}</span>
                        <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                            {getTransferTypeLabel(authDetailBooking?.bookingType, authDetailBooking?.isAirportTransfer)}
                        </p>
                    </div>
                </div>
                <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
                <div className="tw:grid tw:grid-cols-4 tw:gap-x-4 tw:lg:gap-x-[54px] tw:gap-y-4 tw:lg:gap-y-0">
                    <div className="tw:col-span-2 tw:lg:col-span-1 tw:flex tw:w-full tw:lg:w-[200px] tw:flex-col tw:gap-[3px]">
                        <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.pickupTime")}</span>
                        <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                            {moment(authDetailBooking?.pickupDateTime).format(formatDate[currentLanguage])}
                        </p>
                    </div>
                    <div className="tw:col-span-2 tw:lg:col-span-3 tw:flex tw:flex-col tw:gap-[3px]">
                        <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.pickupLocation")}</span>
                        <div className="tw:flex tw:flex-col">
                            <p className="tw:font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                {getLocationDetail(authDetailBooking?.pickupLocation)?.name}
                            </p>
                            <span className={`tw:font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:text-[#7c7b7b] ${typo12}`}>
                                {getLocationDetail(authDetailBooking?.pickupLocation)?.address}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
                <div className="tw:grid tw:grid-cols-4 tw:gap-x-4 tw:lg:gap-x-[54px] tw:gap-y-4 tw:lg:gap-y-0">
                    <div className="tw:col-span-2 tw:lg:col-span-1 tw:flex tw:w-full tw:lg:w-[200px] tw:flex-col tw:gap-[3px]">
                        <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>
                            {t("bookingDetail.generalInformation.item.dropoffTime")}
                        </span>
                        <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                            {moment(authDetailBooking?.customerDropoffDateTime).format(formatDate[currentLanguage])}
                        </p>
                    </div>
                    <div className={`tw:col-span-2 tw:lg:col-span-3 tw:flex tw:flex-col tw:gap-[3px] tw:!w-full`}>
                        {authDetailBooking?.destinations?.map((item, index) => {
                            const isSingle = authDetailBooking?.destinations?.length === 1;
                            const isLast = index === authDetailBooking?.destinations?.length - 1;

                            return (
                                <div
                                    key={index}
                                    className={`tw:flex tw:flex-1 tw:flex-col tw:gap-[3px] ${!isLast ? 'tw:!mb-6' : ''}`}
                                >
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{`${!isSingle ? `${index + 1}.` : ''} ${t('bookingDetail.generalInformation.item.dropoffLocation')}`}</span>
                                    <div className="tw:flex tw:flex-col">
                                        <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                            {getLocationDetail(item)?.name}
                                        </p>
                                        <span className={`tw:font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:text-[#7c7b7b] ${typo12}`}>
                                            {getLocationDetail(item)?.address}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {
                    authDetailBooking?.bookingType === "byTheHour" && (
                        <>
                            <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
                            <div className="tw:grid tw:grid-cols-4 tw:gap-x-[54px]">
                                <div className="tw:lg:col-span-1 tw:col-span-4 tw:mb-4 tw:lg:mb-0 tw:flex tw:w-[200px] tw:flex-col tw:gap-[3px]">
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.travelArea")}</span>
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {`${t("bookingDetail.generalInformation.travelArea." + authDetailBooking?.travelArea)}`}
                                    </p>
                                </div>
                                <div className="tw:lg:col-span-1 tw:col-span-2 tw:flex tw:w-[150px] tw:flex-col tw:gap-[3px]">
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.usageDuration")}</span>
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {`${minutesToHoursCeil(authDetailBooking?.usageDuration)}${t("bookingDetail.generalInformation.item.hours")}`}
                                    </p>
                                </div>
                                <div className="tw:lg:col-span-2 tw:col-span-2 tw:flex tw:w-[150px] tw:flex-col tw:gap-[3px]">
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.billedDuration")}</span>
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {`${minutesToHoursCeil(authDetailBooking?.billedDuration)}${t("bookingDetail.generalInformation.item.hours")}`}
                                    </p>
                                </div>
                            </div>
                        </>
                    )
                }
                <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
                {/* Row 5: Vehicle */}
                {authDetailBooking?.bookingInfo?.length > 1 ? (
                    <>
                        <div className="tw:grid tw:grid-cols-4 tw:gap-x-4 tw:lg:gap-x-[54px] tw:gap-y-4 tw:lg:gap-y-0">
                            {/* <div className="tw:col-span-4 tw:lg:col-span-3 tw:grid tw:grid-cols-1 tw:lg:grid-cols-3 tw:gap-4"> */}
                                {authDetailBooking.bookingInfo.map((item, index) => (
                                    <div key={index} className={`tw:col-span-4 tw:flex tw:w-[200px] tw:flex-col tw:gap-[3px] tw:mb-1 tw:lg:mb-6 ${(index + 1) % 3 === 0 ? 'tw:lg:col-span-2' : 'tw:lg:col-span-1'}`}>
                                        <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{`${t("bookingDetail.generalInformation.item.vehicle")} ${index + 1}`}</span>
                                        <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                            {t(`${item.vehicleTypeName.replace(/ /g, ".")}`)}
                                        </p>
                                    </div>
                                ))}
                            {/* </div> */}
                        </div>
                        <div className="tw:h-px tw:w-full tw:bg-[#e8e8e8]" />
                        <div className="tw:grid tw:grid-cols-4 tw:gap-x-4 tw:lg:gap-x-[54px] tw:gap-y-4 tw:lg:gap-y-0">
                            <div className="tw:col-span-2 tw:lg:col-span-1 tw:flex tw:flex-col tw:gap-[3px]">
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.passenger")}</span>
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {authDetailBooking.pax
                                            .filter((passenger) => passenger.paxcount > 0)
                                            .map((passenger, index, array) => (
                                                <span key={passenger.paxtype}>
                                                    {t(`my_page.bookings_page.booking_item.${passenger.paxtype}`)}: {passenger.paxcount}
                                                    {index !== array.length - 1 ? ", " : ""}
                                                </span>
                                            ))}
                                    </p>
                                </div>
                            <div className="tw:col-span-2 tw:lg:col-span-2 tw:flex tw:flex-col tw:gap-[3px]">
                                <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.luggage")}</span>
                                <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                    {authDetailBooking.luggage.filter((luggage) => luggage.luggagecount > 0).length > 0
                                        ? authDetailBooking.luggage
                                            .filter((luggage) => luggage.luggagecount > 0)
                                            .map((luggage, index, array) => (
                                                <span key={luggage.luggagetype}>
                                                    {luggage.luggagetype == "CHECK-IN SIZE"
                                                        ? t("my_page.bookings_page.booking_item.check_in")
                                                        : t("my_page.bookings_page.booking_item.cabin")}
                                                    : {luggage.luggagecount}
                                                    {index !== array.length - 1 ? ", " : ""}
                                                </span>
                                            ))
                                        : i18n.language === "en" ? <span className="tw:text-[#8d8d8d] tw:italic">None</span> : <span className="tw:text-[#8d8d8d] tw:italic">なし</span>}
                                </p>
                            </div>
                        </div>
                    </>
                ) : (
                    authDetailBooking?.bookingInfo?.map((item, index) => {
                        const isSingle = authDetailBooking?.bookingInfo?.length === 1;
                        const isLast = index === authDetailBooking?.bookingInfo?.length - 1;
                        return (
                            <div
                                key={index}
                                className={`tw:grid tw:grid-cols-4 tw:gap-x-4 tw:lg:gap-x-[54px] tw:gap-y-4 tw:lg:gap-y-0 ${!isLast ? 'tw:!mb-6' : ''}`}
                            >
                                <div className="tw:col-span-4 tw:lg:col-span-1 tw:flex tw:w-[200px] tw:flex-col tw:gap-[3px]">
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{`${!isSingle ? `${index + 1}.` : ''} ${t("bookingDetail.generalInformation.item.vehicle")}`}</span>
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {t(`${item.vehicleTypeName.replace(/ /g, ".")}`)}
                                    </p>
                                </div>
                                <div className="tw:col-span-2 tw:lg:col-span-1 tw:flex tw:w-[150px] tw:flex-col tw:gap-[3px]">
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.passenger")}</span>
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {item.pax
                                            .filter((passenger) => passenger.paxcount > 0)
                                            .map((passenger, index, array) => (
                                                <span key={passenger.paxtype}>
                                                    {t(`my_page.bookings_page.booking_item.${passenger.paxtype}`)}: {passenger.paxcount}
                                                    {index !== array.length - 1 ? ", " : ""}
                                                </span>
                                            ))}
                                    </p>
                                </div>
                                <div className="tw:col-span-2 tw:lg:col-span-2 tw:flex tw:flex-col tw:gap-[3px]">
                                    <span className={`tw:font-hanken-grotesk tw:text-[#a7a7a7] ${typo12}`}>{t("bookingDetail.generalInformation.item.luggage")}</span>
                                    <p className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:!mb-0 tw:leading-[22px] tw:text-[#282828]">
                                        {item.luggage.filter((luggage) => luggage.luggagecount > 0).length > 0
                                            ? item.luggage
                                                .filter((luggage) => luggage.luggagecount > 0)
                                                .map((luggage, index, array) => (
                                                    <span key={luggage.luggagetype}>
                                                        {luggage.luggagetype == "CHECK-IN SIZE"
                                                            ? t("my_page.bookings_page.booking_item.check_in")
                                                            : t("my_page.bookings_page.booking_item.cabin")}
                                                        : {luggage.luggagecount}
                                                        {index !== array.length - 1 ? ", " : ""}
                                                    </span>
                                                ))
                                            : i18n.language === "en" ? <span className="tw:text-[#8d8d8d] tw:italic">None</span> : <span className="tw:text-[#8d8d8d] tw:italic">なし</span>}
                                    </p>
                                </div>
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    );
};

export default GeneralInformation;
